import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { N8nChatService } from './n8n-chat.service';
import { OrganizationService } from './organization.service';
import { ToastService } from './toast.service';
import { WebsocketService } from './websocket.service';

export interface N8nChatMessage {
  content: string;
  receiver: string;
  type: string;
  interactionAnalysisKeypath?: string;
  interactionAnalysisBucket?: string;
}

@Injectable({
  providedIn: 'root',
})
export class N8nChatHandlerService {
  private readonly N8N_MESSAGES_KEY = 'n8n_messages';

  constructor(
    private websocketService: WebsocketService,
    private n8nChatService: N8nChatService,
    private toastService: ToastService,
    private organizationService: OrganizationService,
    private router: Router
  ) {
    this.initializeMessageHandling();
    this.ensureConnectionForPendingRequests();
  }

  private initializeMessageHandling(): void {
    this.websocketService.chatMessage.subscribe((message: N8nChatMessage) => {
      if (message && message.type === 'CHAT') {
        console.log('nova mensagem para o chat recebida');
        this.handleChatMessage(message);
      }
    });
  }

  private ensureConnectionForPendingRequests(): void {
    setTimeout(() => {
      const pendingRequests = this.n8nChatService.getCurrentRequest();
      const organizationAlias =
        this.organizationService.currentOrganization?.alias;

      if (organizationAlias && pendingRequests.isPending) {
        console.log(
          `Found pending N8N requests, ensuring websocket connection...`
        );

        this.websocketService.ensureConnection().then(connected => {
          if (connected) {
            console.log(
              'Websocket connection established for pending N8N requests'
            );
          } else {
            console.warn(
              'Failed to establish websocket connection for pending N8N requests'
            );
          }
        });
      }
    }, 2000);
  }

  private handleChatMessage(message: N8nChatMessage): void {
    this.storeN8nMessage(message);

    const currentRequest = this.n8nChatService.getCurrentRequest();

    if (currentRequest.isPending) {
      this.n8nChatService.completeRequest();
      this.showNotification(message, currentRequest);
    }
  }

  private showNotification(message: N8nChatMessage, pendingRequest: any): void {
    const isOnChatRoute = this.router.url.includes('/chat');
    const isOnInteractionsRoute = this.router.url.includes('/interactions');

    if (
      isOnChatRoute ||
      (isOnInteractionsRoute && pendingRequest.interactionId)
    ) {
      this.toastService.showSuccess('Resposta do Eva recebida!');
    } else {
      this.toastService.showClickableSuccess(
        'Resposta do Eva recebida! Clique aqui para ver a conversa.',
        () => this.navigateToChat(pendingRequest)
      );
    }
  }

  private navigateToChat(pendingRequest: any): void {
    if (pendingRequest.interactionId) {
      this.router.navigate(['/interactions'], {
        queryParams: { id: pendingRequest.interactionId },
      });
    } else {
      this.router.navigate(['/chat']);
    }
  }

  public storeN8nMessage(message: N8nChatMessage): void {
    const organizationAlias =
      this.organizationService.currentOrganization?.alias;
    if (!organizationAlias) return;

    const currentRequest = this.n8nChatService.getCurrentRequest();
    const interactionId = currentRequest?.interactionId || 'general';
    const storageKey = `n8n_chat_${organizationAlias}.${interactionId}`;

    const stored = localStorage.getItem(storageKey);
    const messages = stored ? JSON.parse(stored) : [];

    const chatMessage: any = {
      text: message.content,
      sender: 'system',
    };

    if (message.interactionAnalysisKeypath) {
      chatMessage.interactionAnalysisKeypath =
        message.interactionAnalysisKeypath;
    }

    if (message.interactionAnalysisBucket) {
      chatMessage.interactionAnalysisBucket = message.interactionAnalysisBucket;
    }

    // Garantindo que a mensagem não seja duplicada.
    if (messages.some((msg: any) => msg.text === chatMessage.text)) {
      return;
    }

    messages.push(chatMessage);
    localStorage.setItem(storageKey, JSON.stringify(messages));
  }

  public getStoredN8nMessages(organizationAlias?: string): any[] {
    const alias =
      organizationAlias || this.organizationService.currentOrganization?.alias;
    if (!alias) return [];

    const generalStorageKey = `n8n_chat_${alias}.general`;
    const stored = localStorage.getItem(generalStorageKey);
    return stored ? JSON.parse(stored) : [];
  }

  public getLatestN8nMessage(organizationAlias?: string): any | null {
    const messages = this.getStoredN8nMessages(organizationAlias);
    return messages.length > 0 ? messages[messages.length - 1] : null;
  }

  public clearN8nMessages(organizationAlias?: string): void {
    const alias =
      organizationAlias || this.organizationService.currentOrganization?.alias;
    if (!alias) return;

    const generalStorageKey = `n8n_chat_${alias}.general`;
    localStorage.removeItem(generalStorageKey);

    const oldStorageKey = `${this.N8N_MESSAGES_KEY}_${alias}`;
    localStorage.removeItem(oldStorageKey);
  }

  private generateMessageId(): string {
    return Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
}
